import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { uploadBytes, getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from '../../../firebase';
import { useTranslation } from 'react-i18next';

// Kategori veri tipi
export interface KioskCategory {
  id: string;
  name: string;
  imageUrl?: string;
  order: number;
  showRelatedProductsForAll?: boolean; // Ek ürünleri tüm ürünlerde göster
}

interface KioskCategoriesTabProps {
  categories: KioskCategory[];
  companyId: string | null;
  kioskId: string | null;
  onCategoriesChange: (categories: KioskCategory[]) => void;
  onOpenImageLibrary?: (callback: (imageUrl: string) => void) => void; // ✅ Resim kütüphanesi callback'i
}


const KioskCategoriesTab: React.FC<KioskCategoriesTabProps> = ({
  categories,
  companyId,
  kioskId,
  onCategoriesChange,
  onOpenImageLibrary // ✅ Yeni prop
}) => {
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  
  // Local state'ler
  const [activeCategoryTab, setActiveCategoryTab] = useState<number>(0);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [newCategoryImageUrl, setNewCategoryImageUrl] = useState<string | null>(null); // ✅ File yerine URL
  const [imageUploading, setImageUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Kategori silme onay modalı için state'ler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

  // Kategori resmi Firebase'e yükleme
  const uploadCategoryImageToFirebase = async (file: File, categoryId: string): Promise<string> => {
    try {
      if (!companyId || !kioskId) {
        throw new Error(t('kioskCategoriesTab.errors.requiredIdsNotFound'));
      }

      const imagePath = `companies/${companyId}/kiosks/${kioskId}/categories/${categoryId}/image`;
      const storageReference = storageRef(storage, imagePath);
      const uploadResult = await uploadBytes(storageReference, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      return downloadURL;
    } catch (error) {
      console.error("Kategori resmi Firebase'e yükleme hatası:", error);
      throw error;
    }
  };

  // Kategori ekleme (✅ Resim URL ile)
  const handleAddCategory = async () => {
    if (!newCategoryName.trim() || !newCategoryImageUrl) return;
    
    try {
      setImageUploading(true);
      
      // Yeni kategori ID'si oluştur
      const newCategoryId = `category_${Date.now()}`;
      
      // Kategoriyi ekle (resim URL zaten var)
      const newCategory: KioskCategory = {
        id: newCategoryId,
        name: newCategoryName.trim(),
        imageUrl: newCategoryImageUrl,
        order: categories.length + 1
      };
      
      onCategoriesChange([...categories, newCategory]);
      setCategoryDialogOpen(false);
      setNewCategoryName('');
      setNewCategoryImageUrl(null);
      
      // Yeni eklenen kategoriye geçiş yap
      setActiveCategoryTab(categories.length);
      
      console.log('✅ Yeni kategori eklendi:', newCategory);
    } catch (error) {
      console.error('❌ Kategori ekleme hatası:', error);
      setError(t('kioskCategoriesTab.errors.addCategoryFailed', { message: error instanceof Error ? error.message : t('kioskCategoriesTab.errors.unknownError') }));
    } finally {
      setImageUploading(false);
    }
  };

  // Yeni kategori için resim seçme (✅ Resim kütüphanesinden)
  const handleNewCategoryImageSelect = () => {
    if (onOpenImageLibrary) {
      onOpenImageLibrary((imageUrl: string) => {
        console.log('✅ [KIOSK CATEGORIES] Yeni kategori için resim seçildi:', imageUrl);
        setNewCategoryImageUrl(imageUrl);
      });
    } else {
      console.warn('❌ onOpenImageLibrary prop tanımlı değil');
    }
  };

  // Kategori silme onayı aç
  const handleDeleteCategoryClick = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setDeleteConfirmOpen(true);
  };

  // Kategori silme işlemini onayla
  const handleConfirmDeleteCategory = () => {
    if (categoryToDelete) {
      handleRemoveCategory(categoryToDelete);
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  };

  // Kategori silme (gerçek silme fonksiyonu)
  const handleRemoveCategory = (categoryId: string) => {
    const updatedCategories = categories.filter(cat => cat.id !== categoryId);
    onCategoriesChange(updatedCategories);
    
    // Aktif sekmeyi güncelle
    if (activeCategoryTab >= updatedCategories.length) {
      setActiveCategoryTab(Math.max(0, updatedCategories.length - 1));
    }
  };

  // Kategori sırasını sola taşı (QR Menu gibi)
  const handleMoveCategoryLeft = (index: number) => {
    if (index > 0) {
      const newCategories = [...categories];
      [newCategories[index - 1], newCategories[index]] = [newCategories[index], newCategories[index - 1]];
      
      // Order değerlerini güncelle
      newCategories.forEach((cat, idx) => {
        cat.order = idx + 1;
      });
      
      onCategoriesChange(newCategories);
      setActiveCategoryTab(index - 1); // Aktif tab'ı da kaydır
      console.log('✅ Kategori sola taşındı:', newCategories[index - 1].name);
    }
  };

  // Kategori sırasını sağa taşı (QR Menu gibi)
  const handleMoveCategoryRight = (index: number) => {
    if (index < categories.length - 1) {
      const newCategories = [...categories];
      [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
      
      // Order değerlerini güncelle
      newCategories.forEach((cat, idx) => {
        cat.order = idx + 1;
      });
      
      onCategoriesChange(newCategories);
      setActiveCategoryTab(index + 1); // Aktif tab'ı da kaydır
      console.log('✅ Kategori sağa taşındı:', newCategories[index + 1].name);
    }
  };

  // Kategori resmi güncelleme için modal aç
  const handleCategoryImageUpdate = async (categoryId: string, file: File) => {
    try {
      setImageUploading(true);
      
      // Resmi Firebase'e yükle
      const imageUrl = await uploadCategoryImageToFirebase(file, categoryId);
      
      // Kategori listesini güncelle
      const updatedCategories = categories.map(cat => 
        cat.id === categoryId ? { ...cat, imageUrl } : cat
      );
      onCategoriesChange(updatedCategories);
      
      console.log('✅ Kategori resmi güncellendi:', categoryId);
    } catch (error) {
      console.error('❌ Kategori resmi güncelleme hatası:', error);
      setError(t('kioskCategoriesTab.errors.updateImageFailed', { message: error instanceof Error ? error.message : t('kioskCategoriesTab.errors.unknownError') }));
    } finally {
      setImageUploading(false);
    }
  };

  // Kategori resmi seçme (✅ Resim kütüphanesinden seç)
  const handleCategoryImageSelect = (categoryId: string) => {
    if (onOpenImageLibrary) {
      // Resim kütüphanesi modalını aç
      onOpenImageLibrary((imageUrl: string) => {
        console.log(`✅ [KIOSK CATEGORIES] Kategori ${categoryId} için resim seçildi:`, imageUrl);
        
        // Kategori resmini güncelle
        const updatedCategories = categories.map(cat =>
          cat.id === categoryId
            ? { ...cat, imageUrl: imageUrl }
            : cat
        );
        onCategoriesChange(updatedCategories);
      });
    } else {
      console.warn('❌ onOpenImageLibrary prop tanımlı değil');
    }
  };

  return (
    <>
    <Paper elevation={0} sx={{ p: 2, minHeight: '500px' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('kioskCategoriesTab.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCategoryDialogOpen(true)}
            size="small"
          >
            {t('kioskCategoriesTab.actions.addCategory')}
          </Button>
        </Box>
      </Box>

      {/* Kategori Sekmeleri */}
      {categories.length > 0 ? (
        <>
          {/* 💻 MASAÜSTÜ: Normal Tabs */}
          {!isMobile && (
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              mt: 2, 
              mb: 2,
              display: 'flex',
              alignItems: 'center'
            }}>
              <Tabs
                value={activeCategoryTab}
                onChange={(e, newValue) => setActiveCategoryTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ 
                  flexGrow: 1,
                  '.MuiTab-root': { 
                    textTransform: 'none', 
                    minHeight: 'auto',
                    py: 1
                  }
                }}
              >
                {categories.map((category, index) => (
                  <Tab
                    key={category.id}
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CategoryIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                        {category.name}
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategoryClick(category.id);
                          }}
                          sx={{ ml: 0.5, p: 0.2 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  />
                ))}
              </Tabs>
            </Box>
          )}
          
          {/* 📱 MOBİL: Dropdown Select */}
          {isMobile && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-select-label">{t('kioskCategoriesTab.selectCategory')}</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={activeCategoryTab}
                  label={t('kioskCategoriesTab.selectCategory')}
                  onChange={(e) => setActiveCategoryTab(e.target.value as number)}
                >
                  {categories.map((category, index) => (
                    <MenuItem key={category.id} value={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {category.imageUrl ? (
                            <Avatar src={category.imageUrl} sx={{ width: 24, height: 24 }} />
                          ) : (
                            <CategoryIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                          )}
                          <Typography variant="body2">{category.name}</Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategoryClick(category.id);
                          }}
                          sx={{ p: 0.2 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Kategori İçeriği */}
          {categories.map((category, index) => (
            <Box
              key={category.id}
              role="tabpanel"
              hidden={activeCategoryTab !== index}
              id={`category-tabpanel-${category.id}`}
              aria-labelledby={`category-tab-${category.id}`}
              sx={{ mt: 2 }}
            >
              {activeCategoryTab === index && (
                <Box>
                  {/* Kategori Sıralama Butonları (QR Menu gibi) */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                    <Tooltip title={t('kioskCategoriesTab.tooltips.moveLeft')}>
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() => handleMoveCategoryLeft(index)}
                          disabled={index === 0}
                          size="small"
                          sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            '&:disabled': {
                              borderColor: 'grey.300'
                            }
                          }}
                        >
                          <ArrowBackIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                      {t('kioskCategoriesTab.categoryOrder')}: <strong style={{ marginLeft: '0.5rem' }}>{index + 1} / {categories.length}</strong>
                    </Typography>
                    
                    <Tooltip title={t('kioskCategoriesTab.tooltips.moveRight')}>
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() => handleMoveCategoryRight(index)}
                          disabled={index === categories.length - 1}
                          size="small"
                          sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            '&:disabled': {
                              borderColor: 'grey.300'
                            }
                          }}
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      {/* Sol taraf - Kategori Resmi */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" fontWeight="medium" color="text.secondary">
                          {t('kioskCategoriesTab.categoryImage')}
                        </Typography>
                        
                        {category.imageUrl ? (
                          <Tooltip title={t('kioskCategoriesTab.tooltips.updateImage')}>
                            <Avatar 
                              src={category.imageUrl}
                              alt={category.name}
                              variant="rounded"
                              sx={{ 
                                width: 120, 
                                height: 120,
                                border: '2px solid #eee',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                              onClick={() => handleCategoryImageSelect(category.id)}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('kioskCategoriesTab.tooltips.addImage')}>
                            <Avatar 
                              variant="rounded"
                              sx={{ 
                                width: 120, 
                                height: 120, 
                                bgcolor: 'grey.100',
                                border: '2px dashed #ccc',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: 'grey.200',
                                  borderColor: 'primary.main'
                                }
                              }}
                              onClick={() => handleCategoryImageSelect(category.id)}
                            >
                              <ImageIcon sx={{ fontSize: '3rem', color: 'grey.400' }} />
                            </Avatar>
                          </Tooltip>
                        )}

                        <Button
                          variant="outlined"
                          startIcon={imageUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                          onClick={() => handleCategoryImageSelect(category.id)}
                          size="small"
                          disabled={imageUploading}
                        >
                          {category.imageUrl ? t('kioskCategoriesTab.actions.updateImage') : t('kioskCategoriesTab.actions.addImage')}
                        </Button>
                      </Box>

                      {/* Sağ taraf - Kategori Bilgileri */}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {category.name}
                        </Typography>
                        
                        {/* Bilgi kutusu - Mobilde gizle */}
                        {!isMobile && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              📋 {t('kioskCategoriesTab.info.categoryId')}: <strong>{category.id}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              🔢 {t('kioskCategoriesTab.info.order')}: <strong>{index + 1}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                              💡 {t('kioskCategoriesTab.info.descriptionLeft')}
                            </Typography>
                            <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                              ℹ️ {t('kioskCategoriesTab.info.descriptionArrow')}
                            </Typography>
                          </Box>
                        )}

                        {/* Sonraki adım alert - Mobilde gizle */}
                        {!isMobile && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              <strong>{t('kioskCategoriesTab.nextStep.label')}:</strong> {t('kioskCategoriesTab.nextStep.description')}
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          ))}
        </>
      ) : (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
          <CategoryIcon sx={{ fontSize: '4rem', color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('kioskCategoriesTab.emptyState.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('kioskCategoriesTab.emptyState.description')}
          </Typography>
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCategoryDialogOpen(true)}
          >
            {t('kioskCategoriesTab.actions.addFirstCategory')}
          </Button>
        </Paper>
      )}
    </Paper>

    {/* Kategori Ekleme Dialog */}
    <Dialog open={categoryDialogOpen} onClose={() => {
      setCategoryDialogOpen(false);
      setNewCategoryName('');
      setNewCategoryImageUrl(null);
    }} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('kioskCategoriesTab.addDialog.title')}
        <IconButton
          aria-label={t('kioskCategoriesTab.actions.close')}
          onClick={() => {
            setCategoryDialogOpen(false);
            setNewCategoryName('');
            setNewCategoryImageUrl(null);
          }}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Kategori Adı */}
          <TextField
            autoFocus
            label={t('kioskCategoriesTab.addDialog.nameLabel')}
            type="text"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder={t('kioskCategoriesTab.addDialog.namePlaceholder')}
            sx={{ mb: 3 }}
          />
          
          {/* Kategori Resmi (✅ Resim Kütüphanesinden) */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {t('kioskCategoriesTab.categoryImage')}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              {/* Resim Önizleme */}
              <Avatar
                src={newCategoryImageUrl || undefined}
                variant="rounded"
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'grey.100',
                  border: '2px dashed #ccc',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'grey.200'
                  }
                }}
                onClick={handleNewCategoryImageSelect}
              >
                {!newCategoryImageUrl && <ImageIcon sx={{ fontSize: '2.5rem', color: 'grey.400' }} />}
              </Avatar>
              
              {/* Resim Seçme Butonu (✅ Resim Kütüphanesinden) */}
              <Box sx={{ flex: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  onClick={handleNewCategoryImageSelect}
                  fullWidth
                >
                  {newCategoryImageUrl ? t('kioskCategoriesTab.actions.changeImage') : t('kioskCategoriesTab.actions.selectFromLibrary')}
                </Button>
                
                {newCategoryImageUrl && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="success.main" display="block">
                      ✅ {t('kioskCategoriesTab.imageSelected')}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setNewCategoryImageUrl(null)}
                      sx={{ mt: 0.5 }}
                    >
                      {t('kioskCategoriesTab.actions.removeImage')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {t('kioskCategoriesTab.addDialog.imageNote')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setCategoryDialogOpen(false);
          setNewCategoryName('');
          setNewCategoryImageUrl(null);
        }} color="inherit">
          {t('kioskCategoriesTab.actions.cancel')}
        </Button>
        <Button 
          onClick={handleAddCategory} 
          color="primary" 
          variant="contained"
          disabled={!newCategoryName.trim() || !newCategoryImageUrl || imageUploading}
          startIcon={imageUploading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {imageUploading ? t('kioskCategoriesTab.actions.adding') : t('kioskCategoriesTab.actions.addCategory')}
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Kategori Silme Onay Modalı */}
    <Dialog
      open={deleteConfirmOpen}
      onClose={() => {
        setDeleteConfirmOpen(false);
        setCategoryToDelete(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'warning.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CategoryIcon sx={{ color: 'warning.dark' }} />
          </Box>
          <Typography variant="h6" component="span">
            {t('kioskCategoriesTab.deleteDialog.title')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            ⚠️ {t('kioskCategoriesTab.deleteDialog.warningHeader')}
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
            <li>
              <Typography variant="body2">
                {t('kioskCategoriesTab.deleteDialog.warning1')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('kioskCategoriesTab.deleteDialog.warning2')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('kioskCategoriesTab.deleteDialog.warning3')}
              </Typography>
            </li>
          </Box>
        </Alert>
        
        {categoryToDelete && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('kioskCategoriesTab.deleteDialog.categoryToDelete')}:
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {categories.find(cat => cat.id === categoryToDelete)?.name}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => {
            setDeleteConfirmOpen(false);
            setCategoryToDelete(null);
          }}
          variant="outlined"
          color="inherit"
        >
          {t('kioskCategoriesTab.actions.cancel')}
        </Button>
        <Button
          onClick={handleConfirmDeleteCategory}
          variant="contained"
          color="error"
          startIcon={<CloseIcon />}
        >
          {t('kioskCategoriesTab.actions.yesDelete')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default KioskCategoriesTab;

